import React, {useMemo, useState} from 'react';
import {StyleSheet, css} from 'aphrodite';
import {useNavigate} from 'react-router-dom';
import {FiArrowRight, FiCoffee, FiMinus, FiPackage, FiShoppingBag, FiShoppingCart, FiTrash2, FiTruck} from 'react-icons/fi';

import {useCart} from '../context/CartContext';
import {useDeliveryMode} from '../context/DeliveryModeContext';

const PLATFORM_FEE = 0.05;
const VAT_RATE = 0;

const normalizePrice = price => {
  if (price === null || price === undefined || price === '') return 0;

  const cleaned = typeof price === 'string' ? price.replace(',', '.').trim() : price;
  const numericPrice = Number(cleaned);

  if (Number.isNaN(numericPrice)) return 0;

  if (Number.isInteger(numericPrice) && numericPrice >= 1000) {
    return numericPrice / 100;
  }

  return numericPrice;
};

const formatMoney = value => {
  const numberValue = Number(value) || 0;
  return numberValue.toFixed(2);
};

const getTodayDateValue = () => {
  const date = new Date();
  const year = date.getFullYear();
  const month = `${date.getMonth() + 1}`.padStart(2, '0');
  const day = `${date.getDate()}`.padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const isDineInMode = currentMode => {
  const value = String(currentMode || '').trim().toLowerCase();
  return value === 'dining' || value === 'dine in' || value === 'dine-in';
};

export default function Cart() {
  const navigate = useNavigate();
  const {cartItems, addToCart, removeFromCart, clearCart} = useCart();
  const {mode} = useDeliveryMode();

  const [tip, setTip] = useState('');
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [scheduledDate, setScheduledDate] = useState('');
  const [scheduledTime, setScheduledTime] = useState('');
  const [diningGuests, setDiningGuests] = useState(2);
  const [reservationNotes, setReservationNotes] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  const subtotal = useMemo(() => {
    return cartItems.reduce((sum, item) => {
      return sum + normalizePrice(item.price) * Number(item.quantity || 0);
    }, 0);
  }, [cartItems]);

  const platformFee = subtotal * PLATFORM_FEE;
  const vatAmount = subtotal * VAT_RATE;
  const estimatedTotal = subtotal + platformFee + vatAmount + Number(tip || 0);
  const isDineInOrder = isDineInMode(mode);
  const dineInDeposit =
    isDineInOrder
      ? Math.min(estimatedTotal, Math.round(estimatedTotal * 0.5 * 100) / 100)
      : 0;
  const dineInBalance = Math.max(0, estimatedTotal - dineInDeposit);

  const hasItems = cartItems.length > 0;
  const todayDateValue = getTodayDateValue();

  const handleCheckout = () => {
    const token = localStorage.getItem('token');

    if (!token || token === 'null') {
      navigate('/');
      return;
    }

    if (!hasItems) {
      setErrorMessage('Your cart is empty.');
      return;
    }

    if (mode !== 'Delivery') {
      setShowScheduleModal(true);
      return;
    }

    navigate('/checkout', {
      state: {
        tip: Number(tip || 0),
        orderMode: mode,
      },
    });
  };

  const handleScheduledCheckout = () => {
    if (!scheduledDate.trim() || !scheduledTime.trim()) {
      setErrorMessage(`Please select the ${isDineInOrder ? 'dining' : 'pickup'} date and time.`);
      return;
    }

    setShowScheduleModal(false);

    navigate('/checkout', {
      state: {
        tip: Number(tip || 0),
        scheduledDate: scheduledDate.trim(),
        scheduledTime: scheduledTime.trim(),
        orderMode: mode,
        diningGuests: isDineInOrder ? diningGuests : undefined,
        reservationNotes:
          isDineInOrder ? reservationNotes.trim() : undefined,
        depositAmount: isDineInOrder ? dineInDeposit : undefined,
      },
    });
  };

  return (
    <div className={css(styles.page)}>
      <main className={css(styles.shell)}>
        <section className={css(styles.header)}>
          <div>
            <p className={css(styles.kicker)}>YaBatho Basket</p>
            <h1 className={css(styles.title)}>Your order</h1>
            <p className={css(styles.subtitle)}>Review your items before checkout.</p>
          </div>

          <div className={css(styles.headerBadge)}>
            <span><FiShoppingCart /></span>
            <strong>{cartItems.length}</strong>
          </div>
        </section>

        <section className={css(styles.modeCard)}>
          <div className={css(styles.modeIcon)}>
            {mode === 'Delivery' ? <FiTruck /> : mode === 'Pick-up' ? <FiShoppingBag /> : <FiCoffee />}
          </div>

          <div>
            <h2>{mode} order</h2>
            <p>
              {mode === 'Delivery'
                ? 'Delivery fee will be calculated at checkout after location is selected.'
                : 'No delivery fee will be charged for this order mode.'}
            </p>
          </div>
        </section>

        <section className={css(styles.grid)}>
          <div className={css(styles.card)}>
            <div className={css(styles.cardHeader)}>
              <div>
                <p className={css(styles.sectionKicker)}>Items</p>
                <h2 className={css(styles.sectionTitle)}>Cart items</h2>
              </div>

              {hasItems ? (
                <button type="button" className={css(styles.clearButton)} onClick={clearCart}>
                  Clear
                </button>
              ) : null}
            </div>

            {hasItems ? (
              <div className={css(styles.itemsList)}>
                {cartItems.map(item => {
                  const unitPrice = normalizePrice(item.price);
                  const lineTotal = unitPrice * Number(item.quantity || 0);

                  return (
                    <article key={item.id} className={css(styles.itemRow)}>
                      <div className={css(styles.itemIcon)}><FiPackage /></div>

                      <div className={css(styles.itemContent)}>
                        <h3>{item.name}</h3>
                        <p>R {formatMoney(unitPrice)} × {item.quantity}</p>
                        <strong>R {formatMoney(lineTotal)}</strong>
                      </div>

                      <div className={css(styles.itemControls)}>
                        <button type="button" className={css(styles.quantityButton)} aria-label={`Decrease ${item.name}`} onClick={() => removeFromCart(item.id)}>
                          <FiMinus />
                        </button>

                        <span className={css(styles.quantityValue)}>{item.quantity}</span>

                        <button type="button" className={css(styles.quantityButton)} aria-label={`Increase ${item.name}`} onClick={() => addToCart(item)}>
                          +
                        </button>

                        <button
                          type="button"
                          className={css(styles.trashButton)}
                          onClick={() => removeFromCart(item.id, true)}>
                          <FiTrash2 />
                        </button>
                      </div>
                    </article>
                  );
                })}
              </div>
            ) : (
              <div className={css(styles.emptyBox)}>
                <div className={css(styles.emptyIcon)}><FiShoppingCart /></div>
                <h3 className={css(styles.emptyTitle)}>Your basket is empty</h3>
                <p className={css(styles.emptyCopy)}>Add meals from a local outlet and they will appear here.</p>
                <button type="button" className={css(styles.emptyAction)} onClick={() => navigate('/home')}>
                  Explore outlets <FiArrowRight aria-hidden="true" />
                </button>
              </div>
            )}
          </div>

          <aside className={css(styles.summaryCard)}>
            <p className={css(styles.sectionKicker)}>Summary</p>
            <h2 className={css(styles.sectionTitle)}>Order summary</h2>

            <div className={css(styles.summaryRows)}>
              <div className={css(styles.summaryRow)}>
                <span>Subtotal</span>
                <strong>R {formatMoney(subtotal)}</strong>
              </div>

              <div className={css(styles.summaryRow)}>
                <span>Platform Fee (5%)</span>
                <strong>R {formatMoney(platformFee)}</strong>
              </div>

              <div className={css(styles.summaryRow)}>
                <span>VAT included</span>
                <strong>R {formatMoney(vatAmount)}</strong>
              </div>

              <div className={css(styles.tipRow)}>
                <div className={css(styles.summaryLabelStack)}>
                  <span>Tip optional</span>
                  <small> Optional thank-you amount</small>
                </div>

                <div className={css(styles.tipInputWrap)}>
                  <span className={css(styles.currencyPrefix)}>R</span>
                  <input
                    className={css(styles.tipInput)}
                    value={tip}
                    onChange={event => {
                      const cleaned = event.target.value.replace(',', '.');
                      setTip(cleaned);
                    }}
                    placeholder="0"
                    inputMode="decimal"
                  />
                </div>
              </div>

              {mode === 'Delivery' ? (
                <div className={css(styles.notice)}>
                  Delivery fee is calculated on checkout after your delivery location is selected.
                </div>
              ) : null}

              <div className={css(styles.totalRow)}>
                <div className={css(styles.totalLabelStack)}>
                  <span>{mode === 'Delivery' ? 'Estimated Total' : 'Total'} </span>
                  <small>
                    {mode === 'Delivery'
                      ? 'Excluding delivery fee'
                      : `${mode} order total`}
                  </small>
                </div>

                <strong>R{formatMoney(estimatedTotal)}</strong>
              </div>
            </div>

            {errorMessage ? <p className={css(styles.errorText)}>{errorMessage}</p> : null}

            <button
              type="button"
              className={css(styles.checkoutButton, !hasItems && styles.disabledButton)}
              disabled={!hasItems}
              onClick={handleCheckout}>
              Proceed to Checkout <FiArrowRight />
            </button>
          </aside>
        </section>
      </main>

      {showScheduleModal ? (
        <div className={css(styles.modalOverlay)}>
          <div className={css(styles.modalCard)}>
            <div className={css(styles.modalIcon)}>
              {isDineInOrder ? <FiCoffee /> : <FiShoppingBag />}
            </div>

            <h2>Confirm {mode} Order</h2>
            <p>
              You are placing a <strong>{mode}</strong> order. No delivery fee will be charged.
            </p>

            <div className={css(styles.scheduleGrid)}>
              <label className={css(styles.scheduleField)}>
                <span>{isDineInOrder ? 'Dining Date' : 'Pickup Date'}</span>
                <input
                  className={css(styles.scheduleInput)}
                  type="date"
                  min={todayDateValue}
                  value={scheduledDate}
                  onChange={event => setScheduledDate(event.target.value)}
                />
              </label>

              <label className={css(styles.scheduleField)}>
                <span>{isDineInOrder ? 'Arrival Time' : 'Pickup Time'}</span>
                <input
                  className={css(styles.scheduleInput)}
                  type="time"
                  step="900"
                  value={scheduledTime}
                  onChange={event => setScheduledTime(event.target.value)}
                />
              </label>
            </div>

            {isDineInOrder ? (
              <div className={css(styles.reservationBox)}>
                <label className={css(styles.fieldLabel)}>Guests</label>
                <div className={css(styles.guestStepper)}>
                  <button
                    type="button"
                    onClick={() =>
                      setDiningGuests(current => Math.max(1, current - 1))
                    }>
                    -
                  </button>
                  <strong>
                    {diningGuests} {diningGuests === 1 ? 'guest' : 'guests'}
                  </strong>
                  <button
                    type="button"
                    onClick={() =>
                      setDiningGuests(current => Math.min(30, current + 1))
                    }>
                    +
                  </button>
                </div>

                <label className={css(styles.fieldLabel)}>Reservation notes</label>
                <textarea
                  rows={3}
                  placeholder="Table preference, celebration, allergies..."
                  value={reservationNotes}
                  onChange={event => setReservationNotes(event.target.value)}
                />

                <div className={css(styles.depositBox)}>
                  <div className={css(styles.depositCopy)}>
                    <span>Deposit due now </span>
                    <small>Balance is paid at the restaurant.</small>
                  </div>
                  <div className={css(styles.depositAmountBlock)}>
                    <strong>R {formatMoney(dineInDeposit)}</strong>
                    <small> Balance: R {formatMoney(dineInBalance)}</small>
                  </div>
                </div>
              </div>
            ) : null}

            <div className={css(styles.modalButtons)}>
              <button type="button" className={css(styles.cancelButton)} onClick={() => setShowScheduleModal(false)}>
                Cancel
              </button>

              <button type="button" className={css(styles.confirmButton)} onClick={handleScheduledCheckout}>
                Yes, Proceed
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

const styles = StyleSheet.create({
  page: {
    minHeight: '100vh',
    backgroundColor: '#FFFDF7',
    color: '#0F3D2E',
    fontFamily: 'Inter, Segoe UI, Arial, sans-serif',
    padding: '24px 24px 112px',
    boxSizing: 'border-box',

    '@media (max-width: 720px)': {
      padding: '16px 14px 104px',
    },
  },

  shell: {
    width: '100%',
    maxWidth: '1120px',
    margin: '0 auto',
  },

  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: '18px',
    marginBottom: '18px',
  },

  kicker: {
    margin: '0 0 6px',
    color: '#C9A14A',
    fontSize: '12px',
    fontWeight: '900',
    textTransform: 'uppercase',
    letterSpacing: '1px',
  },

  title: {
    margin: 0,
    color: '#0F3D2E',
    fontSize: '40px',
    lineHeight: 1.05,
    fontWeight: '900',
    letterSpacing: '-1.5px',

    '@media (max-width: 520px)': {
      fontSize: '30px',
    },
  },

  subtitle: {
    margin: '8px 0 0',
    color: '#6E6254',
    fontSize: '14px',
    lineHeight: 1.6,
  },

  headerBadge: {
    minWidth: '58px',
    height: '58px',
    borderRadius: '50%',
    backgroundColor: '#0F3D2E',
    border: '2px solid #C9A14A',
    color: '#ffffff',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '15px',

    strong: {
      fontSize: '11px',
      marginTop: '2px',
    },
  },

  modeCard: {
    backgroundColor: '#F4EBD8',
    border: '1px solid #E6D8BE',
    borderRadius: '24px',
    padding: '15px',
    display: 'flex',
    alignItems: 'center',
    gap: '13px',
    marginBottom: '18px',

    h2: {
      margin: '0 0 4px',
      color: '#0F3D2E',
      fontSize: '16px',
      fontWeight: '900',
    },

    p: {
      margin: 0,
      color: '#6E6254',
      fontSize: '13px',
      lineHeight: 1.5,
    },
  },

  modeIcon: {
    width: '48px',
    height: '48px',
    minWidth: '48px',
    borderRadius: '50%',
    backgroundColor: '#0F3D2E',
    color: '#C9A14A',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },

  grid: {
    display: 'grid',
    gridTemplateColumns: '1fr 380px',
    gap: '18px',
    alignItems: 'flex-start',

    '@media (max-width: 900px)': {
      gridTemplateColumns: '1fr',
    },
  },

  card: {
    backgroundColor: '#ffffff',
    border: '1px solid #E6D8BE',
    borderRadius: '30px',
    padding: '18px',
    boxShadow: '0 12px 34px rgba(30, 30, 30, 0.06)',
  },

  cardHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: '12px',
    marginBottom: '12px',
  },

  sectionKicker: {
    margin: '0 0 5px',
    color: '#C9A14A',
    fontSize: '11px',
    fontWeight: '900',
    textTransform: 'uppercase',
    letterSpacing: '0.9px',
  },

  sectionTitle: {
    margin: 0,
    color: '#0F3D2E',
    fontSize: '22px',
    fontWeight: '900',
  },

  clearButton: {
    border: '1px solid #F5B8B8',
    backgroundColor: '#FFE9E9',
    color: '#D9534F',
    borderRadius: '999px',
    padding: '9px 13px',
    fontWeight: '900',
    cursor: 'pointer',
  },

  itemsList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },

  itemRow: {
    border: '1px solid #F1E7D2',
    backgroundColor: '#FFFDF7',
    borderRadius: '24px',
    padding: '14px',
    display: 'grid',
    gridTemplateColumns: '50px minmax(0, 1fr) auto',
    alignItems: 'center',
    gap: '14px',

    '@media (max-width: 620px)': {
      gridTemplateColumns: '50px minmax(0, 1fr)',
      alignItems: 'center',
    },
  },

  itemIcon: {
    width: '50px',
    height: '50px',
    minWidth: '50px',
    borderRadius: '50%',
    backgroundColor: '#0F3D2E',
    color: '#C9A14A',
    fontSize: '22px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },

  itemContent: {
    flex: 1,
    minWidth: 0,

    h3: {
      margin: '0 0 5px',
      color: '#0F3D2E',
      fontSize: '16px',
      fontWeight: '900',
    },

    p: {
      margin: '0 0 5px',
      color: '#8C8170',
      fontSize: '13px',
    },

    strong: {
      color: '#C9A14A',
      fontSize: '14px',
    },
  },

  itemControls: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',

    '@media (max-width: 620px)': {
      gridColumn: '1 / -1',
      width: '100%',
      justifyContent: 'flex-end',
      paddingTop: '4px',
    },
  },

  quantityButton: {
    width: '38px',
    height: '38px',
    borderRadius: '50%',
    border: 'none',
    backgroundColor: '#C9A14A',
    color: '#0F3D2E',
    fontSize: '18px',
    fontWeight: '900',
    cursor: 'pointer',
    display: 'grid',
    placeItems: 'center',

    ':hover': {
      transform: 'translateY(-1px)',
      boxShadow: '0 8px 18px rgba(201, 161, 74, 0.24)',
    },
  },

  quantityValue: {
    minWidth: '26px',
    textAlign: 'center',
    color: '#0F3D2E',
    fontSize: '15px',
    fontWeight: '900',
  },

  trashButton: {
    width: '38px',
    height: '38px',
    borderRadius: '50%',
    border: '1px solid #F5B8B8',
    backgroundColor: '#FFE9E9',
    color: '#D9534F',
    cursor: 'pointer',
    display: 'grid',
    placeItems: 'center',
    fontSize: '17px',
  },

  emptyBox: {
    backgroundColor: '#FFFDF7',
    border: '1px solid #F1E7D2',
    borderRadius: '26px',
    padding: '34px 18px',
    textAlign: 'center',
  },

  emptyTitle: {
    margin: '0 0 8px',
    color: '#0F3D2E',
    fontSize: '20px',
    fontWeight: '900',
  },

  emptyCopy: {
    margin: '0 auto 18px',
    maxWidth: '360px',
    color: '#6E6254',
    fontSize: '14px',
    lineHeight: 1.7,
  },

  emptyAction: {
    minHeight: '48px',
    border: 'none',
    backgroundColor: '#C9A14A',
    color: '#0F3D2E',
    borderRadius: '999px',
    padding: '13px 22px',
    fontSize: '14px',
    fontWeight: '900',
    fontFamily: 'inherit',
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '9px',
    boxShadow: '0 10px 22px rgba(201, 161, 74, 0.22)',
    transition: 'transform 160ms ease, box-shadow 160ms ease',

    ':hover': {
      transform: 'translateY(-1px)',
      boxShadow: '0 13px 26px rgba(201, 161, 74, 0.3)',
    },

    ':focus-visible': {
      outline: '3px solid rgba(15, 61, 46, 0.22)',
      outlineOffset: '3px',
    },
  },

  emptyIcon: {
    width: '68px',
    height: '68px',
    borderRadius: '50%',
    backgroundColor: '#0F3D2E',
    color: '#C9A14A',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    margin: '0 auto 14px',
    fontSize: '28px',
  },

  summaryCard: {
    backgroundColor: '#ffffff',
    border: '1px solid #E6D8BE',
    borderRadius: '30px',
    padding: '18px',
    boxShadow: '0 12px 34px rgba(30, 30, 30, 0.06)',
    position: 'sticky',
    top: '20px',

    '@media (max-width: 900px)': {
      position: 'relative',
      top: 'auto',
    },
  },

  summaryRows: {
    marginTop: '16px',
  },

  summaryRow: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: '12px',
    padding: '12px 0',
    borderBottom: '1px solid #F1E7D2',

    span: {
      color: '#6E6254',
      fontSize: '14px',
    },

    strong: {
      color: '#0F3D2E',
      fontSize: '14px',
    },
  },

  summaryLabelStack: {
    minWidth: 0,

    span: {
      display: 'block',
      color: '#0F3D2E',
      fontSize: '14px',
      fontWeight: '900',
      lineHeight: 1.35,
    },

    small: {
      display: 'block',
      color: '#8C8170',
      fontSize: '12px',
      marginTop: '4px',
      lineHeight: 1.45,
    },
  },

  tipRow: {
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 1fr) minmax(116px, 150px)',
    gap: '16px',
    alignItems: 'center',
    padding: '14px 0',
    borderBottom: '1px solid #F1E7D2',

    '@media (max-width: 640px)': {
      gridTemplateColumns: '1fr',
      gap: '10px',
      alignItems: 'stretch',
    },
  },

  tipInputWrap: {
    width: '100%',
    height: '46px',
    borderRadius: '18px',
    border: '1px solid #E6D8BE',
    backgroundColor: '#FFFDF7',
    display: 'grid',
    gridTemplateColumns: 'auto minmax(0, 1fr)',
    alignItems: 'center',
    columnGap: '6px',
    padding: '0 12px',
    boxSizing: 'border-box',
    justifySelf: 'end',
    boxShadow: 'inset 0 1px 0 rgba(255, 255, 255, 0.75)',

    '@media (max-width: 640px)': {
      justifySelf: 'stretch',
    },
  },

  currencyPrefix: {
    color: '#8C8170',
    fontWeight: '900',
    flexShrink: 0,
  },

  tipInput: {
    width: '100%',
    minWidth: 0,
    border: 'none',
    outline: 'none',
    backgroundColor: 'transparent',
    color: '#0F3D2E',
    fontWeight: '900',
    fontSize: '14px',
    appearance: 'textfield',
  },

  notice: {
    marginTop: '14px',
    backgroundColor: '#F4EBD8',
    border: '1px solid #E6D8BE',
    color: '#0F3D2E',
    borderRadius: '18px',
    padding: '12px',
    fontSize: '13px',
    lineHeight: 1.5,
  },

  totalRow: {
    marginTop: '14px',
    backgroundColor: '#0f3d2e',
    color: '#ffffff',
    borderRadius: '22px',
    padding: '15px',
    display: 'flex',
    justifyContent: 'space-between',
    gap: '12px',
    alignItems: 'center',

    '@media (max-width: 420px)': {
      alignItems: 'flex-start',
      flexDirection: 'column',
    },

    span: {
      display: 'block',
      color: '#ffffff',
      fontSize: '14px',
      fontWeight: '900',
    },

    small: {
      display: 'block',
      color: '#F4EBD8',
      fontSize: '12px',
      marginTop: '4px',
    },

    strong: {
      color: '#C9A14A',
      fontSize: '22px',
      fontWeight: '900',
      whiteSpace: 'nowrap',
    },
  },

  totalLabelStack: {
    minWidth: 0,
  },

  checkoutButton: {
    width: '100%',
    border: 'none',
    backgroundColor: '#C9A14A',
    color: '#0F3D2E',
    borderRadius: '20px',
    padding: '15px',
    marginTop: '16px',
    fontSize: '15px',
    fontWeight: '900',
    cursor: 'pointer',
  },

  disabledButton: {
    opacity: 0.5,
    cursor: 'not-allowed',
  },

  errorText: {
    margin: '14px 0 0',
    backgroundColor: '#FFE9E9',
    border: '1px solid #F5B8B8',
    color: '#D9534F',
    borderRadius: '16px',
    padding: '10px 12px',
    fontSize: '13px',
    fontWeight: '800',
  },

  modalOverlay: {
    position: 'fixed',
    inset: 0,
    backgroundColor: 'rgba(15, 61, 46, 0.62)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '20px',
    zIndex: 1000,

    '@media (max-width: 560px)': {
      alignItems: 'flex-start',
      padding: '12px',
      overflowY: 'auto',
    },
  },

  modalCard: {
    width: '100%',
    maxWidth: '620px',
    maxHeight: 'calc(100vh - 32px)',
    overflowY: 'auto',
    backgroundColor: '#FFFDF7',
    border: '1px solid #E6D8BE',
    borderRadius: '30px',
    padding: '30px',
    boxShadow: '0 24px 60px rgba(30, 30, 30, 0.22)',

    '@media (max-width: 560px)': {
      borderRadius: '24px',
      padding: '22px',
    },

    h2: {
      margin: '0 0 10px',
      color: '#0F3D2E',
      fontSize: '24px',
      fontWeight: '900',
    },

    p: {
      margin: '0 0 18px',
      color: '#6E6254',
      fontSize: '14px',
      lineHeight: 1.7,
    },

    label: {
      display: 'block',
      color: '#0F3D2E',
      fontSize: '13px',
      fontWeight: '900',
      marginBottom: 0,
    },

    input: {
      width: '100%',
      minHeight: '48px',
      padding: '12px 14px',
      borderRadius: '16px',
      border: '1px solid #E6D8BE',
      backgroundColor: '#ffffff',
      color: '#0F3D2E',
      outline: 'none',
      boxSizing: 'border-box',
      fontFamily: 'inherit',
      fontSize: '15px',
    },

    textarea: {
      width: '100%',
      padding: '13px 14px',
      borderRadius: '16px',
      border: '1px solid #E6D8BE',
      backgroundColor: '#ffffff',
      color: '#0F3D2E',
      outline: 'none',
      boxSizing: 'border-box',
      resize: 'vertical',
      fontFamily: 'inherit',
      fontSize: '15px',
    },
  },

  scheduleGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    gap: '14px',
    marginBottom: '16px',

    label: {
      display: 'grid',
      gap: '8px',
      margin: 0,
    },

    span: {
      color: '#0F3D2E',
      fontSize: '13px',
      fontWeight: '900',
    },

    '@media (max-width: 560px)': {
      gridTemplateColumns: '1fr',
    },
  },

  scheduleField: {
    minWidth: 0,
  },

  scheduleInput: {
    width: '100%',
    minHeight: '54px',
    padding: '12px 14px',
    borderRadius: '18px',
    border: '1px solid #E6D8BE',
    backgroundColor: '#FFFDF7',
    color: '#0F3D2E',
    outline: 'none',
    boxSizing: 'border-box',
    fontFamily: 'inherit',
    fontSize: '15px',
    fontWeight: '800',
    boxShadow: 'inset 0 1px 0 rgba(255, 255, 255, 0.8)',
  },

  fieldLabel: {
    display: 'block',
    color: '#0F3D2E',
    fontSize: '13px',
    fontWeight: '900',
    margin: '0 0 7px',
  },

  reservationBox: {
    backgroundColor: '#ffffff',
    border: '1px solid #E6D8BE',
    borderRadius: '18px',
    padding: '16px',
    marginTop: '4px',
    marginBottom: '14px',
  },

  guestStepper: {
    display: 'grid',
    gridTemplateColumns: '44px 1fr 44px',
    alignItems: 'center',
    gap: '10px',
    backgroundColor: '#F4EBD8',
    borderRadius: '16px',
    padding: '8px',
    marginBottom: '14px',

    button: {
      width: '44px',
      height: '44px',
      borderRadius: '50%',
      border: 0,
      backgroundColor: '#C9A14A',
      color: '#0F3D2E',
      fontSize: '22px',
      fontWeight: '900',
      cursor: 'pointer',
    },

    strong: {
      textAlign: 'center',
      color: '#0F3D2E',
      fontSize: '16px',
      fontWeight: '900',
    },
  },

  depositBox: {
    display: 'grid',
    gridTemplateColumns: '1fr auto',
    alignItems: 'center',
    gap: '18px',
    padding: '16px',
    borderRadius: '16px',
    border: '1px solid #E6D8BE',
    backgroundColor: '#F7F0E3',
    color: '#0F3D2E',

    span: {
      display: 'block',
      fontWeight: '900',
    },

    small: {
      display: 'block',
      color: '#746A5B',
      fontWeight: '800',
      marginTop: '4px',
      lineHeight: 1.45,
    },

    strong: {
      fontSize: '18px',
      whiteSpace: 'nowrap',
    },

    '@media (max-width: 560px)': {
      gridTemplateColumns: '1fr',

      strong: {
        textAlign: 'left',
      },
    },
  },

  depositCopy: {
    minWidth: 0,
  },

  depositAmountBlock: {
    textAlign: 'right',

    strong: {
      display: 'block',
      color: '#0F3D2E',
      fontSize: '20px',
      fontWeight: '900',
    },

    small: {
      display: 'block',
      color: '#746A5B',
      fontSize: '12px',
      fontWeight: '900',
      marginTop: '5px',
      whiteSpace: 'nowrap',
    },

    '@media (max-width: 560px)': {
      textAlign: 'left',
    },
  },

  modalIcon: {
    width: '60px',
    height: '60px',
    borderRadius: '50%',
    backgroundColor: '#0F3D2E',
    color: '#C9A14A',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '26px',
    marginBottom: '14px',
  },

  modalButtons: {
    display: 'flex',
    gap: '12px',
    marginTop: '12px',
  },

  cancelButton: {
    flex: 1,
    border: '1px solid #E6D8BE',
    backgroundColor: '#F4EBD8',
    color: '#0F3D2E',
    borderRadius: '18px',
    padding: '13px',
    fontWeight: '900',
    cursor: 'pointer',
  },

  confirmButton: {
    flex: 1,
    border: 'none',
    backgroundColor: '#C9A14A',
    color: '#0F3D2E',
    borderRadius: '18px',
    padding: '13px',
    fontWeight: '900',
    cursor: 'pointer',
  },
});
